import { useMemo } from 'react';

import { RequestReviewableItemsList } from '@liscio/common';
import {
  Card,
  Stack,
  ThemeProvider,
  Typography,
  styled,
} from '@liscio/ui';

import {
  TaxBalancesDesktopContainer,
  TaxDeliveryDispatchLoadingState,
} from './components';
import { theme } from '../../utils';
import { useGetAllItemsInSections } from 'modules/requests/hooks/useGetAllItemsInSections';
import {
  filterReviewableDocumentItems,
  findBalancesItem,
  findPaymentEstimatesItem,
  parseReviewItemJurisdiction,
} from 'modules/requests/utils';

const Wrapper = styled(Stack)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  flex: 1,
  minHeight: 0,
  padding: theme.spacing(3),
  gap: theme.spacing(4),
  overflow: 'auto',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  paddingRight: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
}));

export type TaxDeliveryRequestMainPanelProps = {
  disabled?: boolean;
};

export const TaxDeliveryRequestMainPanel = ({
  disabled,
}: TaxDeliveryRequestMainPanelProps) => {
  const { items } = useGetAllItemsInSections();

  const balancesItem = useMemo(() => findBalancesItem(items), [items]);
  
  // Group reviewable documents: state-level first, then localities underneath
  const groupedReviewableDocuments = useMemo(() => {
    const allReviewableItems = filterReviewableDocumentItems(items);

    // Separate state-level and locality-level items
    const itemsWithJurisdiction = allReviewableItems.map((item) => {
      const { jurisdiction, isLocality } = parseReviewItemJurisdiction(item.prompt || '');
      return {
        item,
        jurisdiction,
        isLocality,
      };
    });
    
    const stateItems = itemsWithJurisdiction
      .filter((i) => i.jurisdiction && !i.isLocality)
      .sort((a, b) => {
        // Federal (US) always first, then stable sort by jurisdiction
        if (a.jurisdiction === 'US') return -1;
        if (b.jurisdiction === 'US') return 1;
        return a.jurisdiction.localeCompare(b.jurisdiction);
      });

    const localityItems = itemsWithJurisdiction.filter((i) => i.isLocality);
    const unmatchedItems = itemsWithJurisdiction.filter((i) => !i.jurisdiction && !i.isLocality);

    // Track which locality items were matched to a parent state
    const matchedLocalityJurisdictions = new Set(stateItems.map((s) => s.jurisdiction));
    const orphanedLocalities = localityItems.filter(
      (loc) => !matchedLocalityJurisdictions.has(loc.jurisdiction)
    );

    // Group localities under their states
    let itemIndex = 1;

    const toDoc = (i: typeof itemsWithJurisdiction[number], isLocality = false) => {
      const doc = {
        title: `${itemIndex}. ${i.item.prompt}`,
        description: i.item.dependency?.object?.filename || '',
        retrievalToken: i.item.dependency?.object?.retrieval_token || '',
        ...(isLocality && { isLocality: true }),
      };
      itemIndex++;
      return doc;
    };

    const grouped = stateItems.map((stateItem) => {
      const stateLocalities = localityItems
        .filter((loc) => loc.jurisdiction === stateItem.jurisdiction)
        .sort((a, b) => (a.item.prompt ?? '').localeCompare(b.item.prompt ?? ''));
      return {
        state: toDoc(stateItem),
        localities: stateLocalities.map((loc) => toDoc(loc, true)),
      };
    });

    // Append orphaned localities and unmatched items at the end
    // rather than silently dropping them
    const trailingItems = [...orphanedLocalities, ...unmatchedItems];
    if (trailingItems.length > 0) {
      trailingItems.forEach((i) => {
        grouped.push({ state: toDoc(i), localities: [] });
      });
    }

    return grouped;
  }, [items]);

  // Flatten for backward compatibility with RequestReviewableItemsList
  const reviewableDocuments = useMemo(
    () => {
      const flat: Array<{ title: string; description: string; retrievalToken: string; indent?: boolean }> = [];
      groupedReviewableDocuments.forEach(({ state, localities }) => {
        flat.push({ ...state, indent: false });
        flat.push(...localities.map((loc) => ({ ...loc, indent: true })));
      });
      return flat;
    },
    [groupedReviewableDocuments]
  );
  const paymentEstimatesItem = useMemo(
    () => findPaymentEstimatesItem(items),
    [items]
  );

  if (items.length === 0) {
    return (
      <ThemeProvider theme={theme}>
        <TaxDeliveryDispatchLoadingState />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Wrapper>
        {!disabled && <Typography variant="h4">Preview & Send</Typography>}
        <Stack
          gap={4}
          data-tour-id="tax-delivery-details"
          data-tour-min-height="240"
        >
          {balancesItem && (
            <TaxBalancesDesktopContainer
              balancesItem={balancesItem}
              paymentEstimatesItem={paymentEstimatesItem}
            />
          )}
          {reviewableDocuments.length > 0 && (
            <Stack gap={1}>
              <Typography variant="h5" fontWeight={600}>
                Review
              </Typography>
              <StyledCard elevation={0}>
                <RequestReviewableItemsList items={reviewableDocuments} />
              </StyledCard>
            </Stack>
          )}
        </Stack>
      </Wrapper>
    </ThemeProvider>
  );
};
